import { collection, getDocs, doc, getDoc, addDoc, updateDoc, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { User, Role, UserPermission } from '../types';

// Collection names matching mobile app
const USER_REF = 'Users';
const ROLES_REF = 'Roles';

// Check if Firebase is properly initialized
const isFirebaseAvailable = () => {
  try {
    return db && typeof db === 'object';
  } catch (error) {
    console.error('Firebase not available:', error);
    return false;
  }
};

// Get team members by manager ID (using teamMembersReferences approach)
export const getTeamMembersByManagerId = async (managerId: string): Promise<User[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    console.log('🔍 Getting team members for manager:', managerId);
    
    // First, get the manager's data to access teamMembersReferences
    const managerRef = doc(db, USER_REF, managerId);
    const managerSnap = await getDoc(managerRef);
    
    if (!managerSnap.exists()) {
      console.log('❌ Manager not found:', managerId);
      return [];
    }

    const managerData = managerSnap.data();
    console.log('👨‍💼 Manager data:', {
      id: managerId,
      name: `${managerData.firstName} ${managerData.lastName}`,
      teamMembersReferences: managerData.teamMembersReferences || [],
      teamMembersCount: (managerData.teamMembersReferences || []).length
    });

    const teamMembersReferences = managerData.teamMembersReferences || [];
    
    if (teamMembersReferences.length === 0) {
      console.log('📊 No team members found in teamMembersReferences');
      return [];
    }

    // Get team member IDs from references
    const teamMemberIds = teamMembersReferences.map((ref: any) => ref.id);
    console.log('🔍 Team member IDs:', teamMemberIds);

    // Fetch team members by their IDs
    const teamMembers: User[] = [];
    
    for (const memberId of teamMemberIds) {
      try {
        const memberRef = doc(db, USER_REF, memberId);
        const memberSnap = await getDoc(memberRef);
        
        if (memberSnap.exists()) {
          const memberData = memberSnap.data() as User;
          if (!memberData.isArchived && !memberData.isDeleted) {
            console.log('👤 Team member found:', {
              id: memberId,
              name: `${memberData.firstName} ${memberData.lastName}`,
              officesIDsList: memberData.officesIDsList
            });
            teamMembers.push({
              ...memberData,
              id: memberSnap.id
            });
          }
        }
      } catch (error) {
        console.error('Error fetching team member:', memberId, error);
      }
    }
    
    console.log('✅ Total team members found:', teamMembers.length);
    return teamMembers;
  } catch (error) {
    console.error('Error fetching team members by manager ID:', error);
    throw error;
  }
};

// Get team members filtered by office IDs (deprecated - use local filtering instead)
export const getTeamMembersByOfficeIds = async (managerId: string, officeIds: string[]): Promise<User[]> => {
  // This function is deprecated - use getTeamMembersByManagerId and filter locally
  console.warn('⚠️ getTeamMembersByOfficeIds is deprecated. Use getTeamMembersByManagerId and filter locally for better performance.');
  
  const allTeamMembers = await getTeamMembersByManagerId(managerId);
  return allTeamMembers.filter(member => 
    member.officesIDsList && member.officesIDsList.some(officeId => officeIds.includes(officeId))
  );
};

// Get team member roles for a specific office
export const getTeamMemberRoles = async (userId: string, officeId: string): Promise<Role[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    console.log('🔍 Querying roles for user:', userId, 'in office:', officeId);

    // Try the subcollection approach first (like UserRolesContext)
    const rolesCollectionRef = collection(db, 'Users', userId, 'Roles');
    const rolesSnapshot = await getDocs(rolesCollectionRef);
    
    console.log('📋 Found roles in subcollection:', rolesSnapshot.size);
    
    const roles: Role[] = [];
    rolesSnapshot.forEach((roleDoc: QueryDocumentSnapshot) => {
      const roleData = roleDoc.data();
      console.log('🔍 Processing role document:', roleDoc.id, roleData);
      
      // Only include roles for the specified office
      if (roleData.officeID === officeId && !roleData.isArchived && !roleData.isDeleted) {
        const role: Role = {
          id: roleDoc.id,
          officeID: roleData.officeID || '',
          officeName: roleData.officeName || '',
          officeLogoURL: roleData.officeLogoURL || '',
          officePhone: roleData.officePhone || '',
          lawyerID: roleData.lawyerID || '',
          lawyerName: roleData.lawyerName || '',
          lawyerPhotoURL: roleData.lawyerPhotoURL || '',
          isOfficeCreator: roleData.isOfficeCreator || false,
          isOfficeAdmin: roleData.isOfficeAdmin || false,
          subSpecialityID: roleData.subSpecialityID || '1',
          lawyerTitleID: roleData.lawyerTitleID || '1',
          salaryIndicator: roleData.salaryIndicator || 0,
          salaryValue: roleData.salaryValue || 0,
          profitPercentage: roleData.profitPercentage || 0,
          isArchived: roleData.isArchived || false,
          isDeleted: roleData.isDeleted || false,
          clientsReferences: roleData.clientsReferences || [],
          userPermission: roleData.userPermission || {
            id: '',
            showClientsList: false,
            updateClientProfile: false,
            removeClientFromOffice: false,
            showOfficeList: false,
            updateOfficeProfile: false,
            removeOfficeFromOffice: false,
            showEvents: false,
            showEventsList: false,
            updateEvent: false,
            removeEvent: false,
            showReports: false,
            showReportsList: false,
            updateReport: false,
            removeReport: false,
            showClientFilesList: false,
            updateClientFile: false,
            removeClientFile: false,
            showSessionsList: false,
            updateSession: false,
            deleteSession: false,
            showLawyersList: false,
            updateLawyerProfile: false,
            removeLawyerFromOffice: false
          },
          createdAt: roleData.createdAt || '',
          updatedAt: roleData.updatedAt || '',
          createdBy: roleData.createdBy || '',
          updatedBy: roleData.updatedBy || ''
        };
        roles.push(role);
      }
    });
    
    console.log('✅ Final roles for office:', roles.length);
    return roles;
  } catch (error) {
    console.error('Error fetching team member roles:', error);
    throw error;
  }
};

// Update team member permissions
export const updateTeamMemberPermissions = async (
  userId: string, 
  officeId: string, 
  permissions: Partial<UserPermission>
): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    // First get the role for this user in this office
    const roles = await getTeamMemberRoles(userId, officeId);
    if (roles.length === 0) {
      throw new Error('No role found for this user in the specified office');
    }

    // Update the user permission in the role (roles are stored in user subcollection)
    const roleRef = doc(db, 'Users', userId, 'Roles', roles[0].id);
    await updateDoc(roleRef, {
      userPermission: {
        ...roles[0].userPermission,
        ...permissions
      },
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error updating team member permissions:', error);
    throw error;
  }
};

// Add team member to office
export const addTeamMemberToOffice = async (
  userId: string,
  officeId: string,
  roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>
): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const rolesRef = collection(db, ROLES_REF);
    const newRole = {
      ...roleData,
      officeID: officeId,
      lawyerID: userId,
      createdAt: getCurrentMillis(),
      updatedAt: getCurrentMillis(),
      createdBy: roleData.lawyerID, // The manager who is adding the member
      updatedBy: roleData.lawyerID
    };
    
    const docRef = await addDoc(rolesRef, newRole);
    return docRef.id;
  } catch (error) {
    console.error('Error adding team member to office:', error);
    throw error;
  }
};

// Remove team member from office
export const removeTeamMemberFromOffice = async (userId: string, officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const roles = await getTeamMemberRoles(userId, officeId);
    if (roles.length === 0) {
      throw new Error('No role found for this user in the specified office');
    }

    // Soft delete the role
    const roleRef = doc(db, ROLES_REF, roles[0].id);
    await updateDoc(roleRef, {
      isDeleted: true,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error removing team member from office:', error);
    throw error;
  }
};

// Get team member with their roles and permissions for a specific office
export const getTeamMemberWithDetails = async (userId: string, officeId: string): Promise<{
  roles: Role[];
  permissions: UserPermission | null;
} | null> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    console.log('🔍 Getting team member details for:', userId, 'in office:', officeId);

    const roles = await getTeamMemberRoles(userId, officeId);
    const permissions = roles.length > 0 ? roles[0].userPermission : null;

    console.log('✅ Team member details:', { roles: roles.length, permissions: !!permissions });

    return {
      roles,
      permissions
    };
  } catch (error) {
    console.error('Error fetching team member with details:', error);
    throw error;
  }
};

// Search team members by name, email, or phone
export const searchTeamMembers = async (
  managerId: string, 
  officeIds: string[], 
  searchTerm: string
): Promise<User[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const teamMembers = await getTeamMembersByOfficeIds(managerId, officeIds);
    
    // Filter by search term (case insensitive)
    const filteredMembers = teamMembers.filter(member => {
      const searchLower = searchTerm.toLowerCase();
      return (
        member.firstName.toLowerCase().includes(searchLower) ||
        member.lastName.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower) ||
        member.phoneNumber.includes(searchTerm)
      );
    });

    return filteredMembers;
  } catch (error) {
    console.error('Error searching team members:', error);
    throw error;
  }
};
